import React, { useState, useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';
import FriendLocationPanel, { LOCATION_MODE } from './Friendlocationpanel';
import FriendMarker from './Friendmarker';
import useFriendLocationMarkers from './Usefriendlocationmarkers';
import FriendInfoWindow from './Friendinfowindow';
import {
  getFriendsLocation,
  getVisitorLocation,
} from '../../../../services/bioplus-services/friendsLocation';

// Normalizes the business/visitor API shape to match the friends API shape
// so FriendMarker / FriendLocationPanel / FriendInfoWindow can stay unchanged.
const normalizeBusinessLocation = (records = []) => {
  if (!Array.isArray(records)) return [];

  return records?.map((record) => ({
    _id: record?._id,
    location: {
      latitude: record?.latestLocation?.latitude,
      longitude: record?.latestLocation?.longitude,
    },
    updatedAt: record?.latestTimestamp,
    createdAt: record?.latestTimestamp,
    userInfo: {
      _id: record?.userDetails?._id,
      firstName: record?.userDetails?.firstName,
      lastName: record?.userDetails?.lastName,
      email: record?.userDetails?.email,
      phone: record?.userDetails?.phone,
      profileImg: record?.userDetails?.profileImg ?? null,
    },
    companyInfo: {
      _id: record?.companyDetails?._id,
      name: record?.companyDetails?.name,
    },
  }));
};

const FriendLocationContainer = ({
  isViewFriendLocation = false,
  mapRef = null,
  companyId = null,
}) => {
  // State management
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [showFriendInfo, setShowFriendInfo] = useState(false);
  const [friendLocationNames, setFriendLocationNames] = useState({});
  const [loadingLocationFor, setLoadingLocationFor] = useState(null);
  const [locationMode, setLocationMode] = useState(LOCATION_MODE.FRIENDS);

  // Use custom hook for marker interactions
  const { panToFriendMarker, registerMarkerElement, getLocationName, resetAllMarkers } =
    useFriendLocationMarkers(mapRef);

  const isBusinessMode = locationMode === LOCATION_MODE.BUSINESS;

  // Fetch friends location
  const friendsLocationQuery = useQuery(['friendsLocation'], () => getFriendsLocation(), {
    enabled: isViewFriendLocation && !isBusinessMode,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    onError: () => {
      alert('No friend locations available to show.');
    },
  });

  // Fetch business/visitor location
  const businessLocationQuery = useQuery(
    ['visitorLocation', companyId],
    () => getVisitorLocation(companyId),
    {
      enabled: isViewFriendLocation && isBusinessMode && !!companyId,
      refetchOnWindowFocus: false,
      staleTime: 30000,
      onError: () => {
        alert('No business locations available to show.');
      },
    }
  );

  // Normalize business data to the same shape friends data already has
  const friendsLocationData = isBusinessMode
    ? normalizeBusinessLocation(businessLocationQuery?.data)
    : friendsLocationQuery?.data;

  // Reset selection state when switching modes
  useEffect(() => {
    setSelectedFriend(null);
    setShowFriendInfo(false);
    resetAllMarkers();
  }, [locationMode, resetAllMarkers]);

  const handleModeChange = useCallback((mode) => {
    setLocationMode(mode);
  }, []);

  /**
   * Handle friend selection from panel or marker click
   */
  const handleFriendSelection = useCallback(
    async (friend, isMarkerClick = false) => {
      setSelectedFriend(friend);
      setShowFriendInfo(true);

      if (!isMarkerClick) panToFriendMarker(friend);

      if (!friendLocationNames[friend._id] && friend?.location?.latitude != null) {
        setLoadingLocationFor(friend._id);
        try {
          const locationName = await getLocationName(
            friend.location.latitude,
            friend.location.longitude
          );
          setFriendLocationNames((prev) => ({
            ...prev,
            [friend._id]: locationName,
          }));
        } finally {
          setLoadingLocationFor(null);
        }
      }
    },
    [panToFriendMarker, getLocationName, friendLocationNames]
  );

  const handleInfoWindowClose = useCallback(() => {
    setShowFriendInfo(false);
    resetAllMarkers();
    setSelectedFriend(null);
  }, [resetAllMarkers]);

  const enrichedFriendsData =
    friendsLocationData
      ?.filter(
        (friend) => friend?.location?.latitude != null && friend?.location?.longitude != null
      )
      .map((friend) => ({
        ...friend,
        location: {
          ...friend.location,
          displayName: friendLocationNames[friend._id],
        },
      })) || [];

  // Return null if feature is not enabled
  if (!isViewFriendLocation) {
    return null;
  }

  return (
    <>
      {/* Friend/Business Location Panel — always rendered so the dropdown stays visible */}
      <FriendLocationPanel
        friendsData={enrichedFriendsData}
        selectedFriend={selectedFriend}
        onFriendSelect={handleFriendSelection}
        isLoading={
          isBusinessMode ? businessLocationQuery?.isLoading : friendsLocationQuery?.isLoading
        }
        loadingFriendId={loadingLocationFor}
        locationMode={locationMode}
        onModeChange={handleModeChange}
      />

      {/* Markers on Map */}
      {enrichedFriendsData.map((friend) => (
        <FriendMarker
          key={`marker-${friend._id}`}
          friend={friend}
          isSelected={selectedFriend?._id === friend._id}
          onClick={handleFriendSelection}
          onMarkerMount={registerMarkerElement}
          showPulse={selectedFriend?._id === friend._id}
        />
      ))}

      {/* Friend Info Window */}
      {showFriendInfo && selectedFriend && (
        <FriendInfoWindow
          friend={selectedFriend}
          onClose={handleInfoWindowClose}
          locationName={friendLocationNames[selectedFriend._id] || 'Location loaded'}
          isLoadingLocation={loadingLocationFor === selectedFriend._id}
        />
      )}
    </>
  );
};

export default FriendLocationContainer;
